export const FONT_FAMILIES = ['default', 'dyslexia_friendly', 'serif', 'sans'];
export const ANSWER_SPACES = ['normal', 'large', 'extra_large'];
export const CHUNKING = ['none', 'by_section', 'one_at_a_time'];
export const OVERLAY_TINTS = ['none', 'cream', 'blue', 'yellow', 'rose', 'green'];
export const CONTRAST_MODES = ['default', 'high', 'dark'];
export const TIMER_MODES = ['normal', 'extended', 'none'];
export const TTS_SCOPES = ['questions', 'questions_and_options', 'all'];
export const SURFACES = ['quiz', 'worksheet', 'feedback_sheet', 'reader', 'studio', 'lesson'];
export const ASSESSMENT_TYPES = ['formative', 'summative', 'practice'];
export const NEUTRAL_PROFILE = {
    font_family: 'default',
    font_scale: 1,
    line_spacing: 1,
    letter_spacing: 0,
    items_per_page: null,
    single_column: false,
    answer_space: 'normal',
    chunking: 'none',
    overlay_tint: 'none',
    contrast_mode: 'default',
    timer_mode: 'normal',
    time_multiplier: null,
    practice_timer_mode: 'normal',
    practice_time_multiplier: null,
    hide_countdown: false,
    glossary: false,
    simplified_instructions: false,
    tts_enabled: false,
    tts_scope: 'questions',
    tts_voice: null,
    tts_rate: 1,
    reduced_motion: false,
    large_hit_targets: false,
    keyboard_only: false,
};
const OVERLAY_COLORS = {
    none: 'transparent',
    cream: 'rgba(255, 248, 220, 0.45)',
    blue: 'rgba(191, 219, 254, 0.35)',
    yellow: 'rgba(254, 240, 138, 0.40)',
    rose: 'rgba(254, 205, 211, 0.35)',
    green: 'rgba(187, 247, 208, 0.35)',
};
const FONT_STACKS = {
    default: 'inherit',
    dyslexia_friendly: '"Atkinson Hyperlegible", "Comic Sans MS", ui-sans-serif, sans-serif',
    serif: 'Georgia, "Times New Roman", Times, serif',
    sans: 'ui-sans-serif, system-ui, -apple-system, "Segoe UI", sans-serif',
};
export function presentationToCssVars(profile) {
    return {
        '--lp-font-family': FONT_STACKS[profile.font_family] || FONT_STACKS.default,
        '--lp-font-scale': String(profile.font_scale || 1),
        '--lp-line-spacing': String(profile.line_spacing || 1),
        '--lp-letter-spacing': `${profile.letter_spacing || 0}em`,
        '--lp-overlay': OVERLAY_COLORS[profile.overlay_tint] || 'transparent',
    };
}
export function isNeutralProfile(profile) {
    if (!profile)
        return true;
    const keys = [
        'font_family',
        'font_scale',
        'line_spacing',
        'letter_spacing',
        'items_per_page',
        'single_column',
        'answer_space',
        'chunking',
        'overlay_tint',
        'contrast_mode',
        'timer_mode',
        'time_multiplier',
        'tts_enabled',
        'reduced_motion',
        'large_hit_targets',
        'glossary',
    ];
    return keys.every((key) => profile[key] === NEUTRAL_PROFILE[key]);
}
export function textareaRowsForAnswerSpace(answerSpace) {
    if (answerSpace === 'extra_large')
        return 12;
    if (answerSpace === 'large')
        return 8;
    return 4;
}
export function effectiveItemsPerPage(profile) {
    if (!profile)
        return 1;
    if (profile.chunking === 'one_at_a_time')
        return 1;
    if (profile.chunking === 'by_section')
        return 0;
    if (typeof profile.items_per_page === 'number' && profile.items_per_page >= 1) {
        return profile.items_per_page;
    }
    return 1;
}
export function questionSectionKey(question) {
    const raw = question?.section || question?.topic || '';
    return String(raw).trim() || '__default__';
}
export function pageQuestions(questions, startIndex, profile) {
    if (!questions.length)
        return [];
    const idx = Math.max(0, Math.min(startIndex, questions.length - 1));
    if (profile?.chunking === 'by_section') {
        const key = questionSectionKey(questions[idx]);
        const page = [];
        for (let i = idx; i < questions.length; i += 1) {
            if (questionSectionKey(questions[i]) !== key)
                break;
            page.push(questions[i]);
        }
        return page.length ? page : questions.slice(idx, idx + 1);
    }
    const count = effectiveItemsPerPage(profile);
    return questions.slice(idx, idx + Math.max(1, count));
}
export function nextQuestionIndex(questions, startIndex, profile, direction) {
    if (profile?.chunking === 'by_section') {
        const page = pageQuestions(questions, startIndex, profile);
        if (direction > 0) {
            return Math.min(questions.length - 1, startIndex + page.length);
        }
        const prevKey = startIndex > 0 ? questionSectionKey(questions[startIndex - 1]) : null;
        if (!prevKey)
            return 0;
        let i = startIndex - 1;
        while (i > 0 && questionSectionKey(questions[i - 1]) === prevKey)
            i -= 1;
        return i;
    }
    const step = Math.max(1, effectiveItemsPerPage(profile));
    if (direction > 0)
        return Math.min(questions.length - 1, startIndex + step);
    return Math.max(0, startIndex - step);
}
const SNAPSHOT_DIFF_KEYS = [
    'font_family',
    'font_scale',
    'line_spacing',
    'letter_spacing',
    'items_per_page',
    'single_column',
    'answer_space',
    'chunking',
    'overlay_tint',
    'contrast_mode',
    'timer_mode',
    'time_multiplier',
    'effective_timer_mode',
    'effective_time_multiplier',
    'tts_enabled',
    'tts_scope',
    'reduced_motion',
    'large_hit_targets',
    'glossary',
    'simplified_instructions',
    'keyboard_only',
    'hide_countdown',
];
export function snapshotDiffLabels(snapshot) {
    if (!snapshot)
        return [];
    const labels = [];
    const fontScale = Number(snapshot.font_scale);
    if (Number.isFinite(fontScale) && fontScale > 1)
        labels.push(`text ${fontScale}×`);
    if (snapshot.overlay_tint && snapshot.overlay_tint !== 'none')
        labels.push(`${snapshot.overlay_tint} overlay`);
    if (snapshot.tts_enabled === true)
        labels.push('reader');
    const multiplier = Number(snapshot.effective_time_multiplier ?? snapshot.time_multiplier);
    const timerMode = snapshot.effective_timer_mode || snapshot.timer_mode;
    if (timerMode === 'extended' || (Number.isFinite(multiplier) && multiplier > 1)) {
        const extra = Number.isFinite(multiplier) && multiplier > 1 ? Math.round((multiplier - 1) * 100) : 25;
        labels.push(`${extra}% extra time`);
    }
    if (snapshot.chunking && snapshot.chunking !== 'none')
        labels.push(String(snapshot.chunking).replace(/_/g, ' '));
    if (snapshot.simplified_instructions === true)
        labels.push('simplified instructions');
    if (snapshot.keyboard_only === true)
        labels.push('keyboard only');
    if (snapshot.reduced_motion === true)
        labels.push('reduced motion');
    if (snapshot.font_family && snapshot.font_family !== 'default')
        labels.push(String(snapshot.font_family).replace(/_/g, ' '));
    return labels;
}
void SNAPSHOT_DIFF_KEYS;
export function scaledTimerSeconds(baseMinutes, profile) {
    if (!baseMinutes || baseMinutes < 1)
        return null;
    const mode = profile?.effective_timer_mode || profile?.timer_mode || 'normal';
    if (mode === 'none')
        return null;
    const multiplier = mode === 'extended'
        ? Number(profile?.effective_time_multiplier || profile?.time_multiplier || 1.25)
        : 1;
    return Math.round(baseMinutes * 60 * (Number.isFinite(multiplier) ? multiplier : 1));
}
export function glossText(text, terms, enabled) {
    if (!enabled || !text || !terms?.length)
        return text;
    let out = text;
    const sorted = [...terms].sort((a, b) => b.term.length - a.term.length);
    for (const { term, definition } of sorted) {
        if (!term || !definition)
            continue;
        const escaped = term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
        const re = new RegExp(`\\b(${escaped})\\b`, 'i');
        out = out.replace(re, `$1 (${definition})`);
    }
    return out;
}
export function quizAssessmentType(quiz) {
    const raw = quiz?.settings?.assessment_type ||
        quiz?.metadata?.assessment_type ||
        'practice';
    return ASSESSMENT_TYPES.includes(raw) ? raw : 'practice';
}
